import React, { ReactElement, useState, useEffect } from "react"
import { Container, Grid, Header, Message, Icon } from "semantic-ui-react"
import styled from "styled-components"
import { COLOR } from "utils/color"
import {
  DataVisualizationRequestType,
  DataVisualizationState,
  DataVisualizationInitialData,
  DataVisualizationRequest,
} from "types/data-visualization.types"
import DynamicInputForm from "components/DataVisualization/DynamicInputForm"
import ShipperAnalysisTable from "components/DataVisualization/ShipperAnalysisTable"
import { dataVisualizationService } from "services/data-visualization"
import { dataVisualizationInitialService } from "services/data-visualization-initial"
import Title from "components/Title"
import TabMenu from "components/TabsMenu/TabMenu"
import TabsMenu from "components/TabsMenu/TabsMenu"

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f4f4f4;
  padding: 40px;
`

const MainContent = styled.div`
  flex: 1;
  overflow-y: auto;
`

const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  text-align: center;
`

const WelcomeIcon = styled(Icon)`
  font-size: 4rem !important;
  color: ${COLOR.MEDIUM_GREY} !important;
  margin-bottom: 20px !important;
`

const WelcomeTitle = styled.h2`
  color: ${COLOR.DARK_GREY};
  margin-bottom: 10px;
`

const WelcomeText = styled.p`
  color: ${COLOR.MEDIUM_GREY};
  font-size: 16px;
  max-width: 500px;
  line-height: 1.5;
`

const DataContainer = styled.div`
  min-height: 400px;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
`

export default function DataVisualization(): ReactElement {
  const [state, setState] = useState<DataVisualizationState>({
    selectedRequestType: DataVisualizationRequestType.EVOLUTION_OVER_TIME_ANALYSIS, // Auto-select evolution performance analysis
    filterValues: {},
    isLoading: false,
    data: [],
    error: null,
  })

  const [initialData, setInitialData] = useState<DataVisualizationInitialData | null>(null)
  const [initialDataLoading, setInitialDataLoading] = useState(true)
  const [initialDataError, setInitialDataError] = useState<string | null>(null)

  // Load initial data on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      setInitialDataLoading(true)
      setInitialDataError(null)

      try {
        const result = await dataVisualizationInitialService.fetchInitialData()
        if (result.error) {
          setInitialDataError("Erreur lors du chargement des données initiales")
        } else {
          setInitialData(result.data)
        }
      } catch (error) {
        console.error("Error loading initial data:", error)
        setInitialDataError("Erreur lors du chargement des données initiales")
      } finally {
        setInitialDataLoading(false)
      }
    }

    loadInitialData()
  }, [])

  const handleRequestSelect = (requestType: DataVisualizationRequestType) => {
    setState(prev => ({
      ...prev,
      selectedRequestType: requestType,
      filterValues: {},
      data: [],
      error: null,
    }))
  }

  const handleFormSubmit = async (request: DataVisualizationRequest) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const result = await dataVisualizationService.fetchData(state.selectedRequestType!, request)

      if (result.error) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: "Erreur lors de la récupération des données",
        }))
      } else {
        setState(prev => ({
          ...prev,
          isLoading: false,
          data: result.data || [],
        }))
      }
    } catch (error) {
      console.error("Error in handleFormSubmit:", error)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: "Erreur lors de la récupération des données",
      }))
    }
  }

  const renderInitialLoadingScreen = () => (
    <WelcomeContainer>
      <Icon name="spinner" loading size="big" />
      <WelcomeTitle>Chargement des données initiales...</WelcomeTitle>
      <WelcomeText>Récupération de la liste des entreprises, adresses et zones.</WelcomeText>
    </WelcomeContainer>
  )

  const renderInitialErrorScreen = () => (
    <WelcomeContainer>
      <WelcomeIcon name="exclamation triangle" />
      <WelcomeTitle>Erreur de chargement</WelcomeTitle>
      <WelcomeText>{initialDataError}</WelcomeText>
    </WelcomeContainer>
  )

  const renderDataVisualization = () => {
    if (state.isLoading) {
      return (
        <LoadingContainer>
          <Icon name="spinner" loading size="big" />
          <p style={{ marginTop: 20, color: COLOR.MEDIUM_GREY }}>Génération de l&apos;analyse en cours...</p>
        </LoadingContainer>
      )
    }

    if (state.error) {
      return (
        <Message negative>
          <Message.Header>Erreur</Message.Header>
          <p>{state.error}</p>
        </Message>
      )
    }

    if (state.data.length === 0) {
      return (
        <Message info>
          <Message.Header>Aucune donnée</Message.Header>
          <p>Aucune donnée trouvée pour les critères sélectionnés.</p>
        </Message>
      )
    }

    // Render specific visualization based on request type
    if (state.selectedRequestType === DataVisualizationRequestType.EVOLUTION_OVER_TIME_ANALYSIS) {
      return (
        <DataContainer>
          <ShipperAnalysisTable data={state.data as unknown as any} />
        </DataContainer>
      )
    }

    // Basic data display for other request types
    return (
      <DataContainer>
        <Header as="h3">Résultats de l&apos;analyse</Header>
        <pre style={{ background: "#f8f9fa", padding: 20, borderRadius: 4 }}>{JSON.stringify(state.data, null, 2)}</pre>
        <Message info>
          <Message.Header>Données en temps réel</Message.Header>
          <p>
            Les données ci-dessus proviennent de la table parcel_stats. Cette section peut être étendue avec des
            graphiques et visualisations plus avancées.
          </p>
        </Message>
      </DataContainer>
    )
  }

  // Show initial loading screen
  if (initialDataLoading) {
    return (
      <PageContainer>
        <MainContent>
          <Container fluid>{renderInitialLoadingScreen()}</Container>
        </MainContent>
      </PageContainer>
    )
  }

  // Show initial error screen
  if (initialDataError) {
    return (
      <PageContainer>
        <MainContent>
          <Container fluid>{renderInitialErrorScreen()}</Container>
        </MainContent>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <Title>Analyse de données</Title>

      <TabsMenu>
        <TabMenu
          title="Évolution performance"
          active={state.selectedRequestType === DataVisualizationRequestType.EVOLUTION_OVER_TIME_ANALYSIS}
          onClick={() => handleRequestSelect(DataVisualizationRequestType.EVOLUTION_OVER_TIME_ANALYSIS)}
        />
        <TabMenu title="Statistiques globales" active={false} onClick={() => true} />
      </TabsMenu>

      <MainContent>
        <Container fluid>
          {state.selectedRequestType && (
            <Grid>
              <Grid.Row>
                <Grid.Column width={16}>
                  <DynamicInputForm
                    selectedRequestType={state.selectedRequestType}
                    onSubmit={handleFormSubmit}
                    isLoading={state.isLoading}
                    initialData={initialData}
                  />
                </Grid.Column>
              </Grid.Row>

              {(state.data !== null || state.isLoading || state.error) && (
                <Grid.Row style={{ paddingTop: 0 }}>
                  <Grid.Column width={16}>{renderDataVisualization()}</Grid.Column>
                </Grid.Row>
              )}
            </Grid>
          )}
        </Container>
      </MainContent>
    </PageContainer>
  )
}
